<script lang="ts">
  import { onMount } from 'svelte';
  import { ready } from './lib/crypto';
  import { api, ApiError } from './lib/api';
  import { session, setSessionUserOnly } from './lib/session.svelte';
  import { goBack } from './lib/navigate';
  import { logout } from './lib/auth';
  import Login from './components/Login.svelte';
  import Signup from './components/Signup.svelte';
  import Recovery from './components/Recovery.svelte';
  import Home from './components/Home.svelte';
  import Profile from './components/Profile.svelte';
  import Feedback from './components/Feedback.svelte';
  import PublicList from './components/PublicList.svelte';
  import Admin from './components/Admin.svelte';
  import ModerateTags from './components/ModerateTags.svelte';
  import UnlockBanner from './components/UnlockBanner.svelte';
  import ActivityPermalink from './components/ActivityPermalink.svelte';
  import Personvern from './components/Personvern.svelte';
  import TagPage from './components/TagPage.svelte';

  /**
   * URL contract — all in Norwegian:
   *   /                          — public landing (anyone), even when logged in
   *   /hjem                      — authenticated dashboard (private+semi+public)
   *   /aktivitet/:id             — permalink to a single activity (any visibility)
   *   /<username>/liste          — opt-in public list for that user
   *   /personvern                — privacy + how-it-works long-form page
   *   /etiketter/:etikett        — activities matching a tag, scoped to viewer visibility
   *   /invitasjon/:token         — invite-claim landing (routes into signup with token pre-filled)
   *
   * Back-compat: parsePath also accepts the previous English aliases
   * (/home, /a/:id, /<u>/list, /tags/:t, /invite/:t) so links shared
   * before the rename still resolve. Outgoing links always use the
   * Norwegian forms — pushUrl(...) below.
   *
   * Anything else (signup, login, recovery, profile, feedback, admin) is an
   * in-app view state, not a URL. We update window.history on view changes
   * for the URL-backed views so reload / back-button do something sensible.
   */
  type View =
    | 'loading'
    | 'login' | 'signup' | 'recovery'
    | 'public-home'     // "/" — public/semi only, anyone
    | 'home'            // "/hjem" — full authenticated dashboard
    | 'profile' | 'feedback' | 'admin' | 'moderate-tags'
    | 'public-list'     // "/<username>/liste"
    | 'permalink'       // "/aktivitet/:id"
    | 'personvern'      // "/personvern"
    | 'tag';            // "/etiketter/:etikett"

  let view: View = $state('loading');
  let publicListUsername = $state('');
  let activityId = $state('');
  let tagName = $state('');
  let defaultEmail = $state('');
  let inviteToken = $state('');
  let selfRegistryEnabled = $state(true);

  interface Route {
    view: 'public-home' | 'home' | 'public-list' | 'permalink' | 'invite' | 'personvern' | 'tag';
    payload?: string;
  }

  function parsePath(): Route {
    const path = window.location.pathname;
    if (path === '/' || path === '') return { view: 'public-home' };
    if (path === '/hjem' || path === '/hjem/' || path === '/home' || path === '/home/') {
      return { view: 'home' };
    }
    if (path === '/personvern' || path === '/personvern/') return { view: 'personvern' };
    // Etikett-sider: /etiketter/<urlencoded>. We don't constrain charset here
    // because tags allow spaces and any character (they're normalised
    // server-side to lowercase + trim). decodeURIComponent below gets the
    // visible value back. /tags/ is the old English form, still accepted.
    const tagMatch = path.match(/^\/(?:etiketter|tags)\/([^/]+)\/?$/);
    if (tagMatch) {
      try {
        return { view: 'tag', payload: decodeURIComponent(tagMatch[1]!) };
      } catch {
        return { view: 'public-home' };
      }
    }
    const userList = path.match(/^\/([a-z0-9_-]{2,31})\/(?:liste|list)\/?$/);
    if (userList) return { view: 'public-list', payload: userList[1] };
    const perma = path.match(/^\/(?:aktivitet|a)\/([A-Za-z0-9-]+)\/?$/);
    if (perma) return { view: 'permalink', payload: perma[1] };
    const invite = path.match(/^\/(?:invitasjon|invite)\/([A-Za-z0-9_-]+)\/?$/);
    if (invite) return { view: 'invite', payload: invite[1] };
    // Unknown path: treat as the public landing rather than 404. The server
    // returns index.html for anything non-API anyway, so this keeps deep
    // links from looking broken.
    return { view: 'public-home' };
  }

  function pushUrl(path: string) {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  }

  onMount(async () => {
    await ready();

    window.addEventListener('popstate', () => {
      const route = parsePath();
      applyRoute(route);
    });

    const route = parsePath();
    if (route.view === 'public-list' && route.payload) {
      publicListUsername = route.payload;
    }
    if (route.view === 'permalink' && route.payload) {
      activityId = route.payload;
    }
    if (route.view === 'invite' && route.payload) {
      inviteToken = route.payload;
    }

    // Pull public site settings so the UI knows whether to show "Opprett konto".
    api.getSettings()
      .then((s) => { selfRegistryEnabled = s.self_registry_enabled; })
      .catch(() => { /* default open if the call fails */ });

    // Probe the server for an existing session and rehydrate client-side
    // state. The DEK is intentionally NOT persisted (it lives only in
    // memory), so after a reload private rows can't be decrypted until the
    // user re-enters their password. We used to log out here to avoid the
    // "logged in but can't decrypt" state, but that broke permalink
    // navigation: clicking `<a href="/a/<id>">` causes a full reload, this
    // handler ran, and the session was destroyed — kicking the user out
    // mid-click. Now: keep the server session (so non-private content,
    // hearts, bookmarks, etc. all keep working) and just leave dek=null.
    try {
      const me = await api.me();
      defaultEmail = me.email;
      setSessionUserOnly(me);
    } catch {
      // No session — fine.
    }

    // Cold-load redirect: a logged-in user landing on the public landing
    // probably wants their own dashboard, not the marketing-y "what is this"
    // page. We only redirect on this initial mount — not on every
    // applyRoute call — so browser-back from /hjem to / still lets the
    // explicit navigation through (no loop, and the wordmark intentionally
    // sends logged-in users to /hjem instead of / anyway).
    if (route.view === 'public-home' && session.user) {
      pushUrl('/hjem');
      view = 'home';
    } else {
      applyRoute(route);
    }
  });

  function applyRoute(route: Route) {
    if (route.view === 'public-home') view = 'public-home';
    else if (route.view === 'home') view = session.user ? 'home' : 'login';
    else if (route.view === 'public-list') {
      publicListUsername = route.payload ?? publicListUsername;
      view = 'public-list';
    } else if (route.view === 'permalink') {
      activityId = route.payload ?? activityId;
      view = 'permalink';
    } else if (route.view === 'invite') {
      inviteToken = route.payload ?? inviteToken;
      // /invite/<token> drops the visitor straight into the signup form with
      // the token prefilled. If they're already logged in we still show the
      // landing — they can't claim an invite on top of an existing account.
      view = session.user ? 'public-home' : 'signup';
    } else if (route.view === 'personvern') {
      view = 'personvern';
    } else if (route.view === 'tag') {
      tagName = route.payload ?? tagName;
      view = 'tag';
    }
  }

  function goPersonvern() {
    pushUrl('/personvern');
    view = 'personvern';
  }

  /**
   * Back-button handler for sub-views (permalink, tag page, personvern,
   * public list). Uses real browser history so the user returns to
   * wherever they came from in the SPA — /hjem, /etiketter/foo,
   * /aktivitet/bar, anywhere. Falls back to /hjem (or / when anonymous)
   * on cold-loads where there's no prior history entry.
   */
  function backToCallerOrHome() {
    goBack(session.user ? '/hjem' : '/');
  }

  function onAuthed() {
    pushUrl('/hjem');
    view = 'home';
  }

  async function onLogout() {
    await logout();
    pushUrl('/');
    view = 'public-home';
  }

  // Bumped every time goHome / goPublicHome is invoked. Home.svelte
  // watches it via a $effect and re-loads when it changes. This makes
  // clicking the wordmark or "Min liste" while ALREADY on /hjem or /
  // refresh the list — without it, pushUrl is a no-op (same path) and
  // view = 'home' is a no-op (same value), so nothing happened before.
  let reloadKey = $state(0);

  function goHome() {
    pushUrl('/hjem');
    reloadKey++;
    view = 'home';
  }

  function goPublicHome() {
    pushUrl('/');
    reloadKey++;
    view = 'public-home';
  }
</script>

<main>
  <nav class="top">
    <h1 style="margin: 0;">
      <a href={session.user ? '/hjem' : '/'}
         onclick={(e) => { e.preventDefault(); session.user ? goHome() : goPublicHome(); }}
         style="color: inherit; text-decoration: none;">Vinterliste</a>
    </h1>
    {#if view !== 'public-list' && view !== 'permalink' && view !== 'tag'}
      <div class="row">
        {#if session.user}
          {#if view !== 'home'}
            <button type="button" onclick={goHome}>Min liste</button>
          {/if}
          {#if session.user.is_admin}
            <button type="button" onclick={() => (view = 'admin')}>Admin</button>
          {/if}
          {#if session.user.is_moderator || session.user.is_admin}
            <button type="button" onclick={() => (view = 'moderate-tags')}
                    aria-label="Moderer etiketter">
              Etiketter
            </button>
          {/if}
          <button type="button" onclick={() => (view = 'feedback')}
                  aria-label="Send tilbakemelding">
            Tilbakemelding
          </button>
          <button type="button" onclick={() => (view = 'profile')}
                  aria-label="Profil">
            {session.user.display_name?.trim() || 'Profil'}
          </button>
          <button onclick={onLogout}>Logg ut</button>
        {:else if view !== 'login' && view !== 'signup' && view !== 'recovery'}
          <button class="primary" type="button" onclick={() => (view = 'login')}>
            Logg inn
          </button>
        {/if}
      </div>
    {/if}
  </nav>

  <UnlockBanner />

  {#if view === 'loading'}
    <p class="muted">Laster …</p>
  {:else if view === 'public-list'}
    <PublicList username={publicListUsername} onBack={backToCallerOrHome} />
  {:else if view === 'permalink'}
    <ActivityPermalink id={activityId} onBack={backToCallerOrHome} />
  {:else if view === 'public-home'}
    <Home publicOnly={true} reloadKey={reloadKey} />
  {:else if view === 'login'}
    <Login
      defaultEmail={defaultEmail}
      onAuthed={onAuthed}
      onWantSignup={() => (view = 'signup')}
      onWantRecovery={() => (view = 'recovery')}
      signupAvailable={selfRegistryEnabled}
    />
  {:else if view === 'signup'}
    <Signup
      onAuthed={onAuthed}
      onWantLogin={() => (view = 'login')}
      inviteToken={inviteToken}
    />
  {:else if view === 'recovery'}
    <Recovery onAuthed={onAuthed} onWantLogin={() => (view = 'login')} />
  {:else if view === 'profile'}
    <Profile onDone={goHome} />
  {:else if view === 'feedback'}
    <Feedback onDone={goHome} />
  {:else if view === 'admin'}
    <Admin onDone={goHome} />
  {:else if view === 'moderate-tags'}
    <ModerateTags onDone={goHome} />
  {:else if view === 'personvern'}
    <Personvern onBack={backToCallerOrHome} />
  {:else if view === 'tag'}
    <TagPage tag={tagName} onBack={backToCallerOrHome} />
  {:else}
    <Home publicOnly={false} reloadKey={reloadKey} />
  {/if}

  <footer style="margin-top: 3rem; padding-top: 1rem; border-top: 1px solid var(--border); text-align: center;">
    <a href="/personvern"
       onclick={(e) => { e.preventDefault(); goPersonvern(); }}
       class="muted" style="font-size: 0.9rem;">
      Personvern og hvordan det virker
    </a>
  </footer>
</main>
